// The composer under every conversation with the assistant (the Assistant page, the panel, the phone): what you type
// (Enter sends, Shift+Enter is a new line), files you attach (they wait as chips until you say where they go), and the
// microphone. Send while dictating stops, writes it down and sends it, in one go.
import { useEffect, useRef, type FormEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import type { FileItem } from '@dpw/shared';
import { api } from '../api';
import { spring } from '../lib/motion';
import { canRecord, joinWords } from '../lib/recorder';
import { useDictation } from '../voice/useDictation';
import { IconClip, IconSend, IconX } from '../ui/icons';
import { useToast } from '../ui/toast';
import { Waveform } from '../ui/Waveform';
import { P } from '../phone/kit';

export const IconMic = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6v11H9zM5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;
export const IconStop = () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2" /></svg>;

export interface ComposerProps {
  value: string; onChange: (v: string) => void;
  onSend: (text: string) => void;
  onFiles: (files: File[]) => void;
  staged: FileItem[];
  thinking: boolean;
  placeholder: string;
  phone?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  /** Under the staged files on a wide screen. */
  stagedNote?: ReactNode;
  /** Extra controls after the microphone (voice mode). */
  extra?: ReactNode;
  className?: string;
}

/** Grows with what is typed, up to about six lines, then scrolls. */
function autosize(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
}

export function Composer({ value, onChange, onSend, onFiles, staged, thinking, placeholder, phone = false, inputRef, stagedNote, extra, className = '' }: ComposerProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const own = useRef<HTMLTextAreaElement>(null);
  const input = inputRef ?? own;
  const fileInput = useRef<HTMLInputElement>(null);
  const rec = useDictation((t, demo, o) => {
    const full = joinWords(value, t);
    if (demo) toast('Voice is not connected here, so a sample sentence was used.');
    if (o.send) { onSend(full); return; }
    onChange(full);
    input.current?.focus();
  });
  const recording = rec.state === 'recording';
  useEffect(() => { autosize(input.current); }, [value, input]);
  // Drawn while hidden (the dock shows its slot a moment later) it measured nothing: size again once it has a width.
  useEffect(() => {
    const el = input.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== w) { w = el.clientWidth; autosize(el); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, [input]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (recording) { rec.stop({ send: true }); return; }
    if (value.trim() && !thinking) onSend(value);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); }
  };
  const remove = (f: FileItem) => { void api.files.remove(f.id).then(() => qc.invalidateQueries(), (e: unknown) => toast(e instanceof Error ? e.message : 'Could not remove it.')); };
  // Dictating live: your words appear in the box as you say them (read only until you stop). Recording the old way
  // (no live transcription here): the waveform and the time, and the words when it is written down.
  const shown = recording && rec.live ? joinWords(value, rec.said) : value;
  const field = recording && !rec.live
    ? <span className="recrow" role="status"><Waveform bars={rec.bars} level={rec.level} /><span className={phone ? 'mo f13' : 'mono xs'}>{`0:${String(rec.seconds).padStart(2, '0')}`}</span>{!phone && <span className="xs mut">Listening. Stops when you pause, or press Send.</span>}</span>
    : <textarea ref={input} rows={1} value={shown} onChange={e => { if (!recording) onChange(e.target.value); }} onKeyDown={onKey} readOnly={recording}
      placeholder={recording ? 'Listening. Your words appear here as you speak.' : rec.state === 'working' ? 'Writing down what you said' : placeholder} aria-label="Message the assistant" disabled={rec.state === 'working' && !rec.live} enterKeyHint="send" />;
  const meter = recording && rec.live ? <span className="recmini" role="status" aria-label="Listening"><Waveform bars={rec.bars} level={rec.level} /></span> : null;
  const files = <input ref={fileInput} type="file" multiple hidden onChange={e => { onFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />;
  const chips = staged.map(f => (
    <span className="achip" key={f.id}><IconClip />{f.name}
      <button type="button" className="achx" aria-label={`Remove ${f.name}`} onClick={() => remove(f)}><IconX /></button>
    </span>
  ));
  const sendLabel = recording ? 'Stop and send' : 'Send';
  const sendOff = thinking || rec.state === 'working' || (!value.trim() && !recording);

  if (phone) {
    return (
      <form className={`ph-foot ${className}`} onSubmit={submit}>
        {staged.length > 0 && <div className="staged">{chips}</div>}
        <div className={`ph-abar${recording ? ' rec' : ''}`}>
          {field}
          {meter ?? <button type="button" className="mic" aria-label="Attach a file" onClick={() => fileInput.current?.click()}><P.clip /></button>}
          {files}
          {canRecord() && (recording
            ? <button type="button" className="mic" aria-label="Stop and write it down" onClick={() => rec.stop()}><IconStop /></button>
            : <button type="button" className="mic" aria-label="Speak" disabled={rec.state !== 'idle' || thinking} onClick={() => void rec.start()}>{rec.state === 'working' ? <span className="spin" /> : <P.mic />}</button>)}
          {extra}
          <button className="go" aria-label={sendLabel} disabled={sendOff}><P.send /></button>
        </div>
        {rec.error && <div className="err" role="alert">{rec.error}</div>}
      </form>
    );
  }
  return (
    <form className={`compose ${className}`} onSubmit={submit}>
      <AnimatePresence initial={false}>
        {staged.length > 0 && (
          <motion.div className="staged" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }} exit={{ opacity: 0 }}>
            {chips}{stagedNote}
          </motion.div>
        )}
      </AnimatePresence>
      <div className={`aibar composer${recording ? ' rec' : ''}`}>
        {field}
        {meter ?? <button type="button" className="ib" aria-label="Attach a file" onClick={() => fileInput.current?.click()}><IconClip /></button>}
        {files}
        {canRecord() && (recording
          ? <button type="button" className="ib on" aria-label="Stop and write it down" onClick={() => rec.stop()}><IconStop /></button>
          : <button type="button" className="ib" aria-label="Speak" disabled={rec.state !== 'idle' || thinking} onClick={() => void rec.start()}>{rec.state === 'working' ? <span className="spin" /> : <IconMic />}</button>)}
        {extra}
        <motion.button whileTap={{ scale: 0.92 }} transition={spring.snappy} className="ib acc" aria-label={sendLabel} disabled={sendOff}><IconSend /></motion.button>
      </div>
      {rec.error && <div className="err" role="alert">{rec.error}</div>}
    </form>
  );
}
